import toFixedValue, { fromFixedValue } from 'app/common/lib/toFixedValue'

import SimpleDate from 'app/common/classes/simple-date'

import {
  AgeGroupActType,
  CompensationActType,
  KlassesActType,
  DocumentActSubsidiesType,
  RowActData,
  SubsidiesActSection,
  ReportSubsidiesActData,
  SubsidyActSummaryType,
  DayCaresActType,
} from 'app/dataTypes/reports/data/common/subsidies/act'

import {
  ApiSubsidiesAct,
  ActDataApi,
  AllowanceName,
  AgeGroup,
  CompensationPerStudentApi,
  DocumentActSubsidiesApi,
  SummaryApi,
  DataApi,
  AllowanceDayCaresApi
} from './data-api-act.type'

export type ApiResultSubsidiesAct = ApiSubsidiesAct
const dateFormatDto = 'YYYY-MM-DD' as const

export const isRestFoodData = (data: ApiResultSubsidiesAct): data is ApiResultSubsidiesAct => {
  return 'section' in data && data.section === "rest_food"
}

const convertCompensationPerStudent = (compensationPerStudentApi: CompensationPerStudentApi): Array<CompensationActType> => {
  const compensationValues = Object.keys(compensationPerStudentApi)
  return compensationValues.map((compensationValue) => {
    const students = compensationPerStudentApi[compensationValue]
    return {
      compensation: toFixedValue(compensationValue),
      students,
      totalCompensation: toFixedValue(Number(fromFixedValue(compensationValue)) * students),
    }
  })
}

const convertData = (ageGroupApi: Record<AgeGroup, DataApi>): Array<AgeGroupActType> => {
  const ageGroupNames = Object.keys(ageGroupApi)
  return ageGroupNames.map((title) => {
    const ageGroupData = ageGroupApi[title]
    return {
      title,
      total: toFixedValue(ageGroupData.total),
      compensationPerStudent: convertCompensationPerStudent(ageGroupData.compensationPerStudent),
    }
  })
}

const convertKlassesData = (klassesApi: Record<AllowanceName, DataApi>): Array<KlassesActType> => {
  const allowanceNames = Object.keys(klassesApi)
  return allowanceNames.map((title) => {
    const allowanceData = klassesApi[title]
    return {
      title,
      total: toFixedValue(allowanceData.total),
      compensationPerStudent: convertCompensationPerStudent(allowanceData.compensationPerStudent),
    }
  })
}

const convertDayCaresData = (dayCaresApi: Record<AllowanceName, AllowanceDayCaresApi>): Array<DayCaresActType> => {
  const allowanceNames = Object.keys(dayCaresApi)
  return allowanceNames.map((title) => {
    const allowanceData = dayCaresApi[title]
    return {
      title,
      ageGroups: convertData(allowanceData),
    }
  })
}

const convertRows = (rowsData: ActDataApi): RowActData => {
  return {
    klasses: convertKlassesData(rowsData.klasses),
    dayCares: convertDayCaresData(rowsData.day_cares),
  }
}


const convertDocument = (documentApi: DocumentActSubsidiesApi): DocumentActSubsidiesType => {
  return {
    school: documentApi.school,
    month: documentApi.month,
    year: documentApi.year,
    school_sign_label: documentApi.school_sign_label,
    director: documentApi.director,
    foodSupplierAddressLabel: documentApi.food_supplier_address_label,
    foodSupplierAddressValue: documentApi.food_supplier_address_value,
    foodSupplierNameLabel: documentApi.food_supplier_name_label,
    foodSupplierNameValue: documentApi.food_supplier_name_value,
    foodSupplierContactPersonLabel: documentApi.food_supplier_contact_person_label,
    foodSupplierContactPersonValue: documentApi.food_supplier_contact_person_value,
    foodSupplierSignLabel: documentApi.food_supplier_sign_label,
  }
}

const convertSummary = (summaryApi: SummaryApi): SubsidyActSummaryType => {
  const {
    total_sum: totalSum,
    total_students: totalStudents,
    total_subsidy_count: totalSubsidyCount,
    total_klasses_sum: totalKlassesSum,
    total_klasses_students: totalKlassesStudents,
    total_day_cares_sum: totalDayCaresSum,
    total_day_cares_students: totalDayCaresStudents
  } = summaryApi
  return {
    totalSum,
    totalStudents,
    totalSubsidyCount,
    totalKlassesSum,
    totalKlassesStudents,
    totalDayCaresSum,
    totalDayCaresStudents
  }
}

export const convertResultSubsidiesAct = (
  apiData: ApiResultSubsidiesAct,
): ReportSubsidiesActData => {

  if (isRestFoodData(apiData)) {
    const { document, date, rows, summary } = apiData

    return {
      section: SubsidiesActSection.RestFood,
      document: convertDocument(document),
      dateFrom: new SimpleDate(date.from, dateFormatDto),
      dateTo: new SimpleDate(date.to, dateFormatDto),
      rows: convertRows(rows),
      summary: convertSummary(summary)
    }
  }

  const exhaustiveCheck: never = apiData
  return exhaustiveCheck
}
